// src/services/websocketService.js
import { Auth } from 'aws-amplify';
import configService from './configService';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.messageHandlers = [];
    this.connectionHandlers = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.isConnecting = false;
    this.currentUserId = null;
    this.currentSessionId = null;
    this.heartbeatInterval = null;
    this.wsUrl = null;
    this.shouldReconnect = true;
    this.isManualDisconnect = false;
    
    // NEW: Message queuing and recovery
    this.messageQueue = [];
    this.lastMessageTimestamp = null;
    this.isRecoveringMessages = false;
    this.connectionId = null;
    this.reconnectTimeout = null;
    this.pingTimeout = null;
    this.missedPings = 0;
    this.maxMissedPings = 3;
  }

  /**
   * Generate unique connection ID for this browser tab
   */
  generateConnectionId() {
    const stored = sessionStorage.getItem('ws_connection_id');
    if (stored) {
      return stored;
    }
    
    const newId = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('ws_connection_id', newId);
    return newId;
  }

  async getWebSocketUrl() {
    if (this.wsUrl) {
      return this.wsUrl;
    }

    try {
      await configService.fetchConfig();
      this.wsUrl = configService.getWebSocketUrl();
    
      if (!this.wsUrl) {
        console.warn('⚠️ Using hardcoded fallback WebSocket URL');
        this.wsUrl = 'wss://ws.agent-enterprise.com';
      }

      console.log('🔧 WebSocket URL:', this.wsUrl);
      return this.wsUrl;
    } catch (error) {
      console.error('❌ Failed to get WebSocket URL from config:', error);
      this.wsUrl = 'wss://ws.agent-enterprise.com';
      console.log('🔧 Using fallback WebSocket URL:', this.wsUrl);
      return this.wsUrl;
    }
  }

  /**
   * Connect to WebSocket server with authentication
   */
  async connect(userId, sessionId = null) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('⏳ WebSocket connection already in progress');
      return;
    }

    try {
      this.isConnecting = true;
      this.isManualDisconnect = false;
      this.shouldReconnect = true;
      this.currentUserId = userId;
      this.currentSessionId = sessionId;
      this.connectionId = this.generateConnectionId();

      const wsUrl = await this.getWebSocketUrl();
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const params = new URLSearchParams({
        userId: userId,
        token: idToken,
        connectionId: this.connectionId
      });

      if (sessionId) {
        params.append('sessionId', sessionId);
      }

      const fullUrl = `${wsUrl}?${params.toString()}`;
      console.log('🔌 Connecting to WebSocket...');
      
      this.ws = new WebSocket(fullUrl);

      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.error('❌ WebSocket connection timeout');
          this.ws.close();
          this.handleConnectionFailure();
        }
      }, 15000); // Increased to 15 seconds

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        this.handleOpen();
      };

      this.ws.onmessage = this.handleMessage.bind(this);
      
      this.ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('❌ WebSocket error:', error);
        this.handleError(error);
      };
      
      this.ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        this.handleClose(event);
      };

    } catch (error) {
      console.error('❌ WebSocket connection error:', error);
      this.isConnecting = false;
      this.handleConnectionFailure();
    }
  }

  /**
   * Handle WebSocket open event
   */
  async handleOpen() {
    console.log('✅ WebSocket connected successfully');
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.missedPings = 0;
    
    // Clear any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Notify connection handlers
    this.connectionHandlers.forEach(handler => {
      try {
        handler({ type: 'connected' });
      } catch (e) {
        console.error('Error in connection handler:', e);
      }
    });

    // Start heartbeat
    this.startHeartbeat();

    // Subscribe to current session if available
    if (this.currentSessionId) {
      this.subscribe(this.currentSessionId);
    }

    // Process queued messages
    this.processQueuedMessages();

    // Recover missed messages if we have a last timestamp
    if (this.lastMessageTimestamp && this.currentSessionId) {
      await this.recoverMissedMessages();
    }
  }

  /**
   * Process queued messages that were received while disconnected
   */
  processQueuedMessages() {
    if (this.messageQueue.length > 0) {
      console.log(`📦 Processing ${this.messageQueue.length} queued messages`);
      
      const queue = [...this.messageQueue];
      this.messageQueue = [];
      
      queue.forEach(data => {
        this.messageHandlers.forEach(handler => {
          try {
            handler(data);
          } catch (e) {
            console.error('Error in queued message handler:', e);
          }
        });
      });
    }
  }

  /**
   * Recover messages that were sent while disconnected
   */
  async recoverMissedMessages() {
    if (this.isRecoveringMessages) {
      console.log('Already recovering messages');
      return;
    }

    try {
      this.isRecoveringMessages = true;
      console.log('🔄 Recovering missed messages since:', this.lastMessageTimestamp);

      const { makeConversationRequest } = await import('./conversationApi');

      const response = await makeConversationRequest('POST', '/conversation/updates', {
        sessionId: this.currentSessionId,
        since: this.lastMessageTimestamp
      });

      if (response.success && response.newMessages && response.newMessages.length > 0) {
        console.log(`📥 Recovered ${response.newMessages.length} missed messages`);
        
        response.newMessages.forEach(msg => {
          const formattedMessage = {
            type: 'new_message',
            data: {
              messageId: msg.messageId,
              content: msg.content,
              role: msg.role,
              timestamp: msg.timestamp,
              intent: msg.intent,
              ticketId: msg.ticketId
            }
          };

          this.messageHandlers.forEach(handler => {
            try {
              handler(formattedMessage);
            } catch (e) {
              console.error('Error in recovered message handler:', e);
            }
          });
        });
      }
    } catch (error) {
      console.error('Error recovering missed messages:', error);
    } finally {
      this.isRecoveringMessages = false;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      
      // Track last message timestamp for recovery
      if (data.timestamp || (data.data && data.data.timestamp)) {
        this.lastMessageTimestamp = data.timestamp || data.data.timestamp;
      }

      // Handle pong responses - reset missed ping counter
      if (data.action === 'pong' || data.type === 'pong') {
        console.log('💓 Heartbeat pong received');
        this.missedPings = 0;
        return;
      }

      console.log('📨 WebSocket message received:', data.type || data.action || 'unknown');

      // If not fully connected, queue the message
      if (!this.isConnected()) {
        console.log('📦 Queueing message (not connected)');
        this.messageQueue.push(data);
        return;
      }

      // Notify all message handlers
      this.messageHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (e) {
          console.error('Error in message handler:', e);
        }
      });
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Handle WebSocket error
   */
  handleError(error) {
    console.error('❌ WebSocket error:', error);
    this.isConnecting = false;
    
    this.connectionHandlers.forEach(handler => {
      try {
        handler({ type: 'error', error });
      } catch (e) {
        console.error('Error in connection handler:', e);
      }
    });
  }

  /**
   * Handle WebSocket close
   */
  handleClose(event) {
    console.log('🔌 WebSocket closed:', {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean
    });
    
    this.isConnecting = false;
    this.stopHeartbeat();
    this.ws = null;

    this.connectionHandlers.forEach(handler => {
      try {
        handler({ type: 'disconnected', code: event.code, reason: event.reason });
      } catch (e) {
        console.error('Error in connection handler:', e);
      }
    });

    // Attempt reconnection if not a manual disconnect and we should reconnect
    if (!this.isManualDisconnect && this.shouldReconnect) {
      // Don't reconnect immediately on clean close (1000) or going away (1001)
      if (event.code === 1000 || event.code === 1001) {
        console.log('Clean disconnect, waiting before reconnect...');
        setTimeout(() => {
          if (this.shouldReconnect && !this.isManualDisconnect) {
            this.attemptReconnect();
          }
        }, 2000);
      } else {
        this.attemptReconnect();
      }
    }
  }

  /**
   * Handle connection failure
   */
  handleConnectionFailure() {
    this.isConnecting = false;
    this.ws = null;
    
    this.connectionHandlers.forEach(handler => {
      try {
        handler({ type: 'error', error: new Error('Connection failed') });
      } catch (e) {
        console.error('Error in connection handler:', e);
      }
    });

    if (this.shouldReconnect && !this.isManualDisconnect) {
      this.attemptReconnect();
    }
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  attemptReconnect() {
    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      this.shouldReconnect = false;
      
      this.connectionHandlers.forEach(handler => {
        try {
          handler({ type: 'max_reconnect_attempts' });
        } catch (e) {
          console.error('Error in connection handler:', e);
        }
      });
      return;
    }

    if (!this.shouldReconnect || this.isManualDisconnect) {
      console.log('Reconnection disabled, not attempting');
      return;
    }

    this.reconnectAttempts++;
    
    // Exponential backoff with max delay cap
    const delay = Math.min(
      this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );
    
    console.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${Math.round(delay/1000)}s`);

    this.reconnectTimeout = setTimeout(() => {
      if (this.shouldReconnect && !this.isManualDisconnect && this.currentUserId) {
        console.log(`🔄 Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
        this.connect(this.currentUserId, this.currentSessionId);
      }
    }, delay);
  }

  /**
   * Start heartbeat interval with ping timeout detection
   */
  startHeartbeat() {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Increment missed pings
        this.missedPings++;
        
        // If we've missed too many pings, close and reconnect
        if (this.missedPings >= this.maxMissedPings) {
          console.error(`❌ Missed ${this.missedPings} pings, connection appears dead`);
          this.ws.close(4000, 'Heartbeat timeout');
          return;
        }
        
        // Send ping
        this.send({ action: 'ping' });
        console.log(`💓 Sent ping (missed: ${this.missedPings}/${this.maxMissedPings})`);
      }
    }, 30000); // Send ping every 30 seconds
  }

  /**
   * Stop heartbeat interval
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
    
    this.missedPings = 0;
  }

  /**
   * Send data through WebSocket
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
        return true;
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        return false;
      }
    }
    console.warn('WebSocket not connected, cannot send message');
    return false;
  }

  /**
   * Subscribe to a session for updates
   */
  subscribe(sessionId) {
    this.currentSessionId = sessionId;
    const success = this.send({
      action: 'subscribe',
      sessionId: sessionId
    });
    
    if (success) {
      console.log(`📌 Subscribed to session: ${sessionId}`);
    }
    
    return success;
  }

  /**
   * Add a message handler
   */
  addMessageHandler(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Message handler must be a function');
    }
    
    this.messageHandlers.push(handler);
    
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Add a connection status handler
   */
  addConnectionHandler(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Connection handler must be a function');
    }
    
    this.connectionHandlers.push(handler);
    
    return () => {
      const index = this.connectionHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Manually disconnect WebSocket
   */
  disconnect() {
    console.log('🔌 Manually disconnecting WebSocket');
    
    this.isManualDisconnect = true;
    this.shouldReconnect = false;
    this.stopHeartbeat();
    
    // Clear any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.ws) {
      try {
        this.ws.close(1000, 'Client disconnecting');
      } catch (error) {
        console.error('Error closing WebSocket:', error);
      }
      this.ws = null;
    }
    
    this.currentUserId = null;
    this.currentSessionId = null;
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.missedPings = 0;
  }

  /**
   * Force reconnect
   */
  forceReconnect() {
    console.log('🔄 Forcing reconnect...');
    
    this.isManualDisconnect = false;
    this.shouldReconnect = true;
    
    if (this.ws) {
      this.ws.close(1000, 'Forcing reconnect');
    }
    
    this.reconnectAttempts = 0;
    
    if (this.currentUserId) {
      setTimeout(() => {
        this.connect(this.currentUserId, this.currentSessionId);
      }, 1000);
    }
  }

  /**
   * Get connection status
   */
  getStatus() {
    if (!this.ws) {
      return 'disconnected';
    }
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  /**
   * Remove a message handler
   */
  removeMessageHandler(handler) {
    const index = this.messageHandlers.indexOf(handler);
    if (index > -1) {
      this.messageHandlers.splice(index, 1);
    }
  }

  /**
   * Remove a connection handler
   */
  removeConnectionHandler(handler) {
    const index = this.connectionHandlers.indexOf(handler);
    if (index > -1) {
      this.connectionHandlers.splice(index, 1);
    }
  }

  /**
   * Clear message queue
   */
  clearMessageQueue() {
    this.messageQueue = [];
  }

  /**
   * Reset connection state (useful for debugging)
   */
  reset() {
    console.log('🔄 Resetting WebSocket service');
    
    this.disconnect();
    this.messageHandlers = [];
    this.connectionHandlers = [];
    this.messageQueue = [];
    this.lastMessageTimestamp = null;
    this.isRecoveringMessages = false;
    this.reconnectAttempts = 0;
    this.isManualDisconnect = false;
    this.shouldReconnect = true;
    
    sessionStorage.removeItem('ws_connection_id');
    this.connectionId = null;
  }
}

const websocketService = new WebSocketService();
export default websocketService;
